/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as authApi from "../api/auth";
import * as orgApi from "../api/organisations";
import { setSessionExpiredHandler } from "../api/client";
import { redirectToAccountsAfterLogout } from "../utils/authEnvironment";

const AuthContext = createContext(null);

/**
 * Session state lives here rather than in Redux because the session is an
 * httpOnly cookie the client cannot read. `/auth/me` is the only source of
 * truth, so auth is a fetch, not a stored token.
 *
 * Drive does not sign anyone in — accounts does, then hands over the cookie.
 * This provider only observes whether that cookie is currently valid.
 *
 * `status` is explicitly tri-state. The previous implementation checked a
 * never-assigned `authChecked` flag, so "still checking" and "definitely logged
 * out" were indistinguishable and every refresh flashed the login screen.
 */
export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState("checking"); // checking | authenticated | anonymous
  const [user, setUser] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);

  const loadOrganisations = useCallback(async () => {
    try {
      const { organisations: rows, activeOrganisationId } = await orgApi.listOrganisations();
      setOrganisations(rows);
      setActiveOrgId(activeOrganisationId);
    } catch {
      // An org-less account is normal, not an error — the switcher just hides.
      setOrganisations([]);
      setActiveOrgId(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      // Organisations load BEFORE flipping to authenticated. Consumers derive
      // org-scoped state (isOrgAdmin, the team list, the switcher) from them, so
      // an "authenticated but orgs still empty" window reads as "not an admin"
      // and would bounce a real admin out of /admin before the list arrives.
      const me = await authApi.getMe();
      await loadOrganisations();
      setUser(me);
      setStatus("authenticated");
    } catch {
      setUser(null);
      setStatus("anonymous");
      setOrganisations([]);
      setActiveOrgId(null);
    }
  }, [loadOrganisations]);

  /**
   * Switching org changes what org-shared content is visible, and the server
   * scopes by the session's active org — so reload rather than trying to
   * invalidate every cached list in the tree.
   */
  const switchOrganisation = useCallback(async (organisationId) => {
    await orgApi.setActiveOrganisation(organisationId);
    window.location.reload();
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // The axios interceptor calls this when a refresh attempt fails. Dropping to
  // `anonymous` sends ProtectedRoute to /auth/login, which bounces to accounts.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setStatus("anonymous");
    });
    return () => setSessionExpiredHandler(() => {});
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setStatus("anonymous");
      // Accounts owns the identity session, so finish sign-out over there —
      // clearing only drive's cookie would leave the user still signed in.
      redirectToAccountsAfterLogout();
    }
  }, []);

  const activeOrg = organisations.find((o) => o.organisation_id === activeOrgId) || null;

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        isAuthenticated: status === "authenticated",
        isChecking: status === "checking",
        organisations,
        activeOrgId,
        activeOrg,
        // Derived, not fetched. /auth/organisations already returns the caller's
        // role per org, and it reads the same vcs_user_organisations.role that
        // /drive/admin/me does — so asking the server again told us nothing new.
        // This only decides whether to *show* admin entry points; every admin
        // endpoint re-checks the role server-side, so it is not the boundary.
        isOrgAdmin: activeOrg?.role === "admin",
        switchOrganisation,
        logout,
        refreshUser: bootstrap,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
